<script setup>
import jwtDecode from "jwt-decode";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import HeaderTitle from "@/components/layout/HeaderTitle";
import SecuritySectionTitle from "@/components/security/SecuritySectionTitle.vue";
import defaultEmployeeImage from "@/assets/images/user.png";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import EmployeeService from "@/api/services/employee.service";
import ImpersonationService from "@/api/services/impersonation.service";
import Notification from "@/utils/plugins/NotificationService";
import { buildImpersonatedUser } from "@/utils/impersonationSession";
import { saveOriginalSession, applyImpersonatedSession } from "@/utils/impersonationStorage";

const authStore = useAuthStore();
const uiStore = useUiStore();
const router = useRouter();
const employees = ref([]);
const selectedEmployeeId = ref(null);
const starting = ref(false);
const activeUserImage = ref(defaultEmployeeImage);
const targetImage = ref(defaultEmployeeImage);

const activeUser = computed(() => {
  const user = authStore.getUserLogged || {};
  const fullName = [user.name, user.lastName].filter(Boolean).join(" ") || user.fullName || "Usuario activo";

  return {
    fullName,
    role: user.jobName || user.job?.name || "—",
  };
});

const selectedEmployee = computed(
  () => employees.value.find((employee) => employee.id === selectedEmployeeId.value) || null,
);

const getEmployeeImage = (employee) => {
  if (!employee?.photo) {
    return defaultEmployeeImage;
  }

  if (typeof employee.photo === "object" && employee.photo.contentUrl) {
    return `${process.env.VUE_APP_BASE_URI}${employee.photo.contentUrl}`;
  }

  if (typeof employee.photo === "string") {
    return `${process.env.VUE_APP_BASE_URI}/images/employees/${employee.photo}`;
  }

  return defaultEmployeeImage;
};

const useActiveUserDefaultImage = () => {
  activeUserImage.value = defaultEmployeeImage;
};

const useTargetDefaultImage = () => {
  targetImage.value = defaultEmployeeImage;
};

const loadEmployees = async () => {
  uiStore.startLoading();

  try {
    const response = await EmployeeService.getEmployees({
      active: 1,
      itemsPerPage: 200,
      "order[fullName]": "ASC",
    });

    employees.value = (response?.data?.["hydra:member"] || []).filter(
      (employee) => employee.id !== authStore.getUserLogged?.id && employee.userId,
    );
  } finally {
    uiStore.stopLoading();
  }
};

const onEmployeeSelected = () => {
  targetImage.value = getEmployeeImage(selectedEmployee.value);
};

const cancel = () => {
  selectedEmployeeId.value = null;
  targetImage.value = defaultEmployeeImage;
  router.push({ name: "security" });
};

const startImpersonation = async () => {
  if (!selectedEmployee.value) {
    Notification.warning("Selecciona el empleado al que quieres suplantar.", 3500);
    return;
  }

  starting.value = true;
  try {
    const res = await ImpersonationService.start("User", selectedEmployee.value.userId);
    const decoded = jwtDecode(res.data.token);
    const impersonatedUser = buildImpersonatedUser(selectedEmployee.value, decoded.roles);

    saveOriginalSession();
    applyImpersonatedSession(res.data.token, impersonatedUser);

    Notification.success(`Suplantando a ${selectedEmployee.value.fullName}.`, 3500);
    window.location.reload();
  } catch (err) {
    const message = err?.response?.data?.error ?? "No se ha podido iniciar la suplantación.";
    Notification.error(message, 4500);
  } finally {
    starting.value = false;
  }
};

onMounted(() => {
  activeUserImage.value = getEmployeeImage(authStore.getUserLogged);
  loadEmployees();
});
</script>

<template>
  <section class="impersonations">
    <HeaderTitle title="Seguridad" />
    <SecuritySectionTitle title="Suplantar usuario" />

    <el-alert
      class="impersonations__notice"
      title="La suplantación queda registrada en la auditoría de Seguridad. Úsala solo cuando sea necesaria."
      type="warning"
      :closable="false"
      show-icon
    />

    <el-card class="impersonation-form" body-style="padding: 0">
      <div class="impersonation-form__heading">
        <h2>Suplantar usuario</h2>
      </div>

      <div class="impersonation-form__content">
        <div class="form-item">
          <label class="w-100">Usuario real (quien suplantará)</label>
          <div class="employee-summary">
            <img :src="activeUserImage" alt="" @error="useActiveUserDefaultImage" />
            <div>
              <b>{{ activeUser.fullName }}</b>
              <span>{{ activeUser.role }}</span>
            </div>
          </div>
        </div>

        <div class="form-item">
          <label class="w-100" for="impersonated-employee">Usuario objetivo (a suplantar)</label>
          <el-select
            id="impersonated-employee"
            v-model="selectedEmployeeId"
            class="employee-selector w-100"
            placeholder="Selecciona el empleado a suplantar"
            filterable
            clearable
            popper-class="impersonation-employee-popper"
            @change="onEmployeeSelected"
          >
            <template #prefix>
              <img
                v-if="selectedEmployee"
                class="employee-selector__avatar"
                :src="targetImage"
                alt=""
                @error="useTargetDefaultImage"
              />
            </template>
            <el-option
              v-for="employee in employees"
              :key="employee.id"
              :label="employee.fullName"
              :value="employee.id"
            >
              <div class="employee-option">
                <img
                  :src="getEmployeeImage(employee)"
                  alt=""
                  @error="($event) => ($event.target.src = defaultEmployeeImage)"
                />
                <div>
                  <b>{{ employee.fullName }}</b>
                  <span>{{ employee.job?.name || "Sin puesto asignado" }}</span>
                </div>
              </div>
            </el-option>
          </el-select>
        </div>

        <div v-if="selectedEmployee" class="employee-summary employee-summary--target">
          <img :src="targetImage" alt="" @error="useTargetDefaultImage" />
          <div>
            <b>{{ selectedEmployee.fullName }}</b>
            <span>{{ selectedEmployee.job?.name || "Sin puesto asignado" }}</span>
          </div>
        </div>

        <p class="impersonation-duration-note">
          La suplantación dura como máximo <b>30 minutos</b> (duración fija, no configurable). Pasado ese tiempo
          tendrás que iniciar sesión de nuevo con tu cuenta.
        </p>

        <div class="impersonation-form__actions">
          <button type="button" class="btn btn_red" :disabled="!selectedEmployee || starting" @click="startImpersonation">
            {{ starting ? "SUPLANTANDO..." : "SUPLANTAR USUARIO" }}
          </button>
          <button type="button" class="btn btn_yellow" @click="cancel">CANCELAR</button>
        </div>
      </div>
    </el-card>
  </section>
</template>

<style lang="scss" scoped>
.impersonations {
  color: #30385d;
}

.impersonations__notice {
  margin: 8px 0 22px;
}

.impersonation-form {
  background: #fff !important;
  border-radius: 15px;
  box-shadow: 0 5px 18px #30385d1a;
  margin: 0 auto;
  max-width: 760px;
}

.impersonation-form__heading {
  border-bottom: 1px solid #e4e6f0;
  padding: 20px 28px 14px;
}

.impersonation-form__heading h2 {
  color: #474ce6;
  font-size: 1.5rem;
  font-weight: 900;
  margin: 0;
}

.impersonation-form__content {
  padding: 22px 28px 28px;
}

.form-item {
  margin-bottom: 18px;
}

.impersonation-duration-note {
  background: #fff5d8;
  border: 1px solid #f7d20d;
  border-radius: 8px;
  color: #715e22;
  font-size: .82rem;
  margin: 0 0 18px;
  padding: 8px 12px;
}

.form-item label {
  margin-bottom: 7px;
}

.employee-summary {
  align-items: center;
  border: 1px solid #e1e3ed;
  border-radius: 12px;
  display: flex;
  gap: 12px;
  min-height: 74px;
  padding: 10px 14px;
}

.employee-summary--target {
  background: #f8f8ff;
  border-color: #cdd0ff;
  margin: -4px 0 18px;
}

.employee-summary img,
.employee-option img,
.employee-selector__avatar {
  border: 2px solid #aeb4ff;
  border-radius: 50%;
  height: 48px;
  object-fit: cover;
  width: 48px;
}

.employee-summary div,
.employee-option div {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.employee-summary b,
.employee-option b {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-summary span,
.employee-option span {
  color: #7c83a2;
}

.employee-option {
  align-items: center;
  display: flex;
  gap: 10px;
  min-height: 50px;
}

.employee-option img {
  height: 38px;
  width: 38px;
}

.employee-selector__avatar {
  height: 28px;
  width: 28px;
}

:deep(.employee-selector .el-select__wrapper) {
  padding-left: 12px;
}

.impersonation-form__actions {
  display: flex;
  gap: 14px;
  justify-content: flex-end;
  margin-top: 8px;
}

.impersonation-form__actions .btn {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  margin: 0 !important;
  min-height: 44px;
}

.impersonation-form__actions .btn_red {
  color: #fff !important;
}

.impersonation-form__actions .btn_yellow,
.impersonation-form__actions .btn_yellow:hover,
.impersonation-form__actions .btn_yellow:focus-visible {
  color: #474ce6 !important;
}

.impersonation-form__actions .btn:disabled {
  cursor: not-allowed;
  opacity: .5;
}

:global(.impersonation-employee-popper .el-select-dropdown__item) {
  height: auto;
  line-height: normal;
  min-height: 56px;
  padding: 7px 12px;
}

:global(.impersonation-employee-popper .el-select-dropdown__item.is-hovering),
:global(.impersonation-employee-popper .el-select-dropdown__item.is-selected) {
  background: #f1f2ff;
}

@media (max-width: 767px) {
  .impersonation-form__heading,
  .impersonation-form__content {
    padding-left: 16px;
    padding-right: 16px;
  }

  .impersonation-form__actions {
    flex-direction: column;
  }

  .impersonation-form__actions .btn {
    width: 100%;
  }
}
</style>
